import classNames from "classnames";
import { useEffect, useState } from "react";
import { Col, Row } from "react-grid-system";
import { getDisplayError } from "../../lib/api/endpoints";
import {
  GetAccountDetailsResponse,
  GetAccountResponse,
  PaymentMethod,
} from "../../lib/api/models";
import {
  useGetPartyRestrictions,
  useGetPaymentMethods,
} from "../../lib/api/queries";
import { ToolState, ToolType } from "../AccountDetails";
import { toolTypeToName } from "../AccountDetails/utils";
import Container from "../common/Container";
import Spinner from "../common/Spinner";
import ToolLoadError from "../common/ToolLoadError";
import AddSourceAccount from "./AddSourceAccount";
import EditSourceAccount from "./EditSourceAccount";
import toolStyles from "../../sharedStyles/Tool.module.css";
import styles from "./ManageSourceAccounts.module.css";
import ViewSourceAccounts from "./ViewSourceAccounts/ViewSourceAccounts";
import StyledButton from "../common/buttons/StyledButton";
import { getStylizedPaymentMethodName } from "../../lib/sourceAccounts";
import { UseQueryResult } from "@tanstack/react-query";

const strings = {
  close: "Close",
  newSourceAccount: "New Source Account",
  b2c: "B2C Restricted",
  c2c: "C2C Restricted",
  save: "Save",
  addNew: "Add New Source Account",
};

enum SourceAccountMode {
  View = "View",
  Edit = "Edit",
  Add = "Add",
}

type SourceAccountModeState =
  | {
      mode: SourceAccountMode.View | SourceAccountMode.Add;
    }
  | { mode: SourceAccountMode.Edit; paymentMethod: PaymentMethod };

type HeaderProps = {
  isLoadingError: boolean;
  currentMode: SourceAccountModeState;
  onRequestClose: () => void;
  setCurrentMode: React.Dispatch<React.SetStateAction<SourceAccountModeState>>;
};

function Header({
  isLoadingError,
  currentMode,
  onRequestClose,
  setCurrentMode,
}: HeaderProps) {
  const renderExtraHeader = () => {
    if (isLoadingError) {
      return <Col md={9} />;
    }
    switch (currentMode.mode) {
      case SourceAccountMode.View:
        return (
          <Col md={2} offset={{ md: 7 }} className={toolStyles.centerCol}>
            <StyledButton
              className={styles.newAccountButton}
              variant="secondary"
              renderLoadingIndicator={false}
              onClick={() => setCurrentMode({ mode: SourceAccountMode.Add })}
            >
              {strings.newSourceAccount}
            </StyledButton>
          </Col>
        );
      case SourceAccountMode.Add:
        return (
          <Col md={9} className={toolStyles.centerCol}>
            <div className={styles.subtitle}>{strings.addNew}</div>
          </Col>
        );
      case SourceAccountMode.Edit:
        return (
          <Col md={9} className={toolStyles.centerCol}>
            <div className={styles.subtitle}>
              {getStylizedPaymentMethodName(currentMode.paymentMethod)}
            </div>
          </Col>
        );
    }
  };

  return (
    <Row className={toolStyles.header}>
      <Col md={2} className={toolStyles.centerCol}>
        <h2 className={toolStyles.title}>
          {toolTypeToName(ToolType.SourceAccounts)}
        </h2>
      </Col>
      {renderExtraHeader()}
      <Col md={1} className={toolStyles.centerCol}>
        <StyledButton
          className={toolStyles.headerButton}
          variant="secondary"
          renderLoadingIndicator={false}
          onClick={onRequestClose}
        >
          {strings.close}
        </StyledButton>
      </Col>
    </Row>
  );
}

type Props = {
  account: GetAccountResponse;
  accountDetailsQueryResult: UseQueryResult<GetAccountDetailsResponse>;
  className?: string;
  onSetToolInProgress: (id: string, inProgress: boolean) => void;
  tool: ToolState;
  onDisplayGeneralErrors: (err: unknown) => void;
  onRequestClose: () => void;
};

export default function ManageSourceAccounts({
  account: cardmemberAccount,
  accountDetailsQueryResult: {
    data: accountDetails,
    isLoading: isGetAccountDetailsLoading,
    isError: isGetAccountDetailsError,
    error: getAccountDetailsError,
    refetch: getAccountDetailsRefetch,
  },
  className,
  onSetToolInProgress,
  tool,
  onDisplayGeneralErrors,
  onRequestClose,
}: Props) {
  // Refetch data on load to ensure freshness
  useEffect(() => {
    getAccountDetailsRefetch();
  }, [getAccountDetailsRefetch]);

  const getPaymentMethodsQuery = useGetPaymentMethods({
    partyId: cardmemberAccount.partyId,
  });
  const getPartyRestrictionsQuery = useGetPartyRestrictions(
    cardmemberAccount.partyId,
  );
  const isQueryLoading =
    getPaymentMethodsQuery.isLoading ||
    isGetAccountDetailsLoading ||
    getPartyRestrictionsQuery.isLoading;
  const isQueryError =
    getPaymentMethodsQuery.isError ||
    isGetAccountDetailsError ||
    getPartyRestrictionsQuery.isError;
  const [currentMode, setCurrentMode] = useState<SourceAccountModeState>({
    mode: SourceAccountMode.View,
  });
  useEffect(() => {
    onSetToolInProgress(tool.id, currentMode.mode !== SourceAccountMode.View);
  }, [currentMode, onSetToolInProgress, tool.id]);

  return (
    <div className={classNames(toolStyles.container, className)} id={tool.id}>
      <Container>
        <Header
          isLoadingError={isQueryLoading || isQueryError}
          currentMode={currentMode}
          onRequestClose={onRequestClose}
          setCurrentMode={setCurrentMode}
        />
        {isQueryLoading ? (
          <Row>
            <Col offset={{ xs: 6 }} xs={1}>
              <Spinner className={styles.spinner} />
            </Col>
          </Row>
        ) : isQueryError ? (
          <Row>
            <Col>
              <ToolLoadError
                toolName={toolTypeToName(ToolType.SourceAccounts)}
                errorDescription={[
                  getPaymentMethodsQuery.error
                    ? getDisplayError(getPaymentMethodsQuery.error)?.message
                    : "",
                  getAccountDetailsError
                    ? getDisplayError(getAccountDetailsError)?.message
                    : "",
                  getPartyRestrictionsQuery.error
                    ? getDisplayError(getPartyRestrictionsQuery.error)?.message
                    : "",
                ].join(" ")}
                onRetry={() => {
                  // FIXME: A bit cleaner if we only refetch failed queries
                  getPaymentMethodsQuery.refetch();
                  getAccountDetailsRefetch();
                  getPartyRestrictionsQuery.refetch();
                }}
              />
            </Col>
          </Row>
        ) : currentMode.mode === SourceAccountMode.View ? (
          <ViewSourceAccounts
            cardmemberAccount={cardmemberAccount}
            onEditPaymentMethod={(paymentMethod) =>
              setCurrentMode({
                mode: SourceAccountMode.Edit,
                paymentMethod,
              })
            }
            partyRestrictions={getPartyRestrictionsQuery.data}
            paymentMethods={getPaymentMethodsQuery.data}
          />
        ) : currentMode.mode === SourceAccountMode.Add ? (
          <AddSourceAccount
            cardmemberAccount={cardmemberAccount}
            cardmemberAccountDetails={accountDetails}
            onSuccess={() => setCurrentMode({ mode: SourceAccountMode.View })}
            onCancel={() => setCurrentMode({ mode: SourceAccountMode.View })}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
          />
        ) : currentMode.mode === SourceAccountMode.Edit ? (
          <EditSourceAccount
            restrictions={getPartyRestrictionsQuery.data}
            cardmemberAccount={cardmemberAccount}
            cardmemberAccountDetails={accountDetails}
            paymentMethod={currentMode.paymentMethod}
            onCancel={() => setCurrentMode({ mode: SourceAccountMode.View })}
            onSuccess={() => setCurrentMode({ mode: SourceAccountMode.View })}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
          />
        ) : null}
      </Container>
    </div>
  );
}
